import { useState } from "react";
import { HelpCircle, Send, User, Mail, MessageSquare, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import { createFaqQuestion } from "../../lib/api";
import { placeholder, EMAIL_REGEX, FAQ_DEFAULT_CATEGORY, DEFAULT_EMPTY_FAQ_QUESTION } from "../../lib/constants";

export default function Faq({ items = [], sectionContent, elementVisibility, faqCategories = [], uiStrings, sectionId }) {
  const faqSection = uiStrings?.faq_section || {};
  const eyebrow = faqSection.eyebrow || sectionContent?.eyebrow || placeholder("short");
  const headline = faqSection.headline || sectionContent?.headline || placeholder("medium");
  const description = faqSection.description || sectionContent?.description || placeholder("long");

  const catMap = {};
  faqCategories.forEach(c => { catMap[c.uid] = c.name; });

  const grouped = items.reduce((acc, faq) => {
    const catName = faq.category_uid ? (catMap[faq.category_uid] || FAQ_DEFAULT_CATEGORY) : FAQ_DEFAULT_CATEGORY;
    if (!acc[catName]) acc[catName] = [];
    acc[catName].push(faq);
    return acc;
  }, {});
  const categoryOrder = Object.keys(grouped);
  const [openIds, setOpenIds] = useState([]);
  function toggleOpen(uid) {
    setOpenIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  }
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_EMPTY_FAQ_QUESTION);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.question.trim()) {
      return toast.error(placeholder("short"));
    }
    if (!EMAIL_REGEX.test(form.email)) {
      return toast.error(placeholder("short"));
    }
    setSending(true);
    try {
      await createFaqQuestion({ ...form });
      setSent(true);
      toast.success(placeholder("short"));
    } catch (err) {
      toast.error(placeholder("short"));
    } finally {
      setSending(false);
    }
  }

  return (
    <section data-id="site-faq" id={sectionId} className="bg-fendi/30 py-28 md:py-40">
      <div data-id="faq-container" className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-16 md:mb-20">
          <p className="eyebrow text-warmgray" data-id="faq-eyebrow">{eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]" data-id="faq-headline">
            {headline}
          </h2>
          <p className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed mt-4 md:mt-6 font-light max-w-2xl mx-auto" data-id="faq-description">
            {description}
          </p>
        </Reveal>

        {elementVisibility?.faq_items !== false && (
        <div data-id="faq-items-wrapper" className="max-w-6xl mx-auto">
            {items.length === 0 ? (
              <p data-id="faq-empty" className="text-center text-warmgray text-sm">{placeholder("short")}</p>
            ) : (
              <div data-id="faq-columns" className="md:columns-2 md:gap-x-10 space-y-10 md:space-y-0">
                {categoryOrder.map((cat) => (
                  <div key={cat} data-id={`faq-category-group-${cat.toLowerCase().replace(/\s+/g, "-")}`} className="break-inside-avoid mb-10">
                    <p className="text-xs uppercase tracking-[0.25em] text-softblack font-semibold mb-5 text-center" data-id={`faq-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}>{cat}</p>
                    <div data-id={`faq-category-items-${cat.toLowerCase().replace(/\s+/g, "-")}`} className="space-y-3">
                      {grouped[cat].map((faq, idx) => (
                        <Reveal key={faq.uid} delay={idx * 60}>
                          <div data-id={`faq-card-${faq.uid}`} className={`bg-white border rounded-lg overflow-hidden transition-all duration-300 ${openIds.includes(faq.uid) ? 'border-champagne shadow-md' : 'border-fendi/20 hover:border-fendi/40 hover:shadow-md'}`}>
                            <button data-id={`faq-toggle-${faq.uid}`}
                              onClick={() => toggleOpen(faq.uid)}
                              className="w-full text-left p-4 md:p-5 cursor-pointer"
                            >
                              <p className="text-sm md:text-[15px] text-softblack font-medium leading-snug flex items-start gap-3" data-id={`faq-question-${faq.uid}`}>
                                <span data-id={`faq-num-${faq.uid}`} className="mt-0.5 w-6 h-6 rounded-full bg-champagne/10 text-champagne flex items-center justify-center shrink-0 text-[10px] font-serif">
                                  {idx + 1}
                                </span>
                                <span data-id={`faq-question-text-${faq.uid}`} className="flex-1">{faq.question}</span>
                                <span data-id={`faq-chevron-${faq.uid}`} className={`mt-0.5 text-champagne transition-transform duration-300 ${openIds.includes(faq.uid) ? 'rotate-180' : ''}`}>
                                  <svg data-id={`faq-chevron-icon-${faq.uid}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"/>
                                  </svg>
                                </span>
                              </p>
                            </button>
                            <div data-id={`faq-answer-wrapper-${faq.uid}`} className={`transition-all duration-300 overflow-hidden ${openIds.includes(faq.uid) ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div data-id={`faq-answer-border-${faq.uid}`} className="border-t border-fendi/10 mx-4 md:mx-5">
                                <p className="text-sm text-warmgray leading-relaxed font-light pt-3 pb-4 md:pb-5" data-id={`faq-answer-${faq.uid}`}>{faq.answer}</p>
                              </div>
                            </div>
                          </div>
                        </Reveal>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

          {/* Divider */}
          {elementVisibility?.faq_form !== false && (
            <>
              <div data-id="faq-divider" className="flex items-center gap-4 max-w-md mx-auto mt-16 md:mt-20 mb-10">
                <div data-id="faq-divider-left" className="flex-1 h-px bg-fendi/40" />
                <span data-id="faq-divider-text" className="text-[10px] uppercase tracking-[0.3em] text-warmgray shrink-0">{placeholder("short")}</span>
                <div data-id="faq-divider-right" className="flex-1 h-px bg-fendi/40" />
              </div>

              <Reveal className="max-w-2xl mx-auto">
                {!showForm && !sent && (
                  <div data-id="faq-cta-wrapper" className="text-center">
                    <button data-id="faq-cta-button"
                      type="button"
                      onClick={() => setShowForm(true)}
                      className="inline-flex items-center gap-2.5 px-6 py-3 bg-white border border-fendi/30 text-sm text-softblack hover:border-champagne hover:text-champagne transition-all uppercase tracking-[0.2em]"
                    >
                      <HelpCircle size={14} /> {placeholder("short")}
                    </button>
                  </div>
                )}

                {sent && (
                  <div data-id="faq-success" className="text-center p-10 bg-white border border-fendi/30">
                    <CheckCircle size={36} className="text-champagne mx-auto mb-4" />
                    <p data-id="faq-success-title" className="text-sm text-softblack font-medium">{placeholder("short")}</p>
                    <p data-id="faq-success-desc" className="text-xs text-warmgray mt-2">{placeholder("medium")}</p>
                    <button data-id="faq-success-reset"
                      type="button"
                      onClick={() => { setSent(false); setShowForm(false); setForm({ ...DEFAULT_EMPTY_FAQ_QUESTION }); }}
                      className="mt-6 inline-flex items-center gap-1.5 text-xs text-champagne hover:text-softblack transition-colors uppercase tracking-[0.2em]"
                    >
                      <Send size={11} /> {placeholder("short")}
                    </button>
                  </div>
                )}

                {showForm && !sent && (
                  <form data-id="faq-form" onSubmit={handleSubmit} className="bg-white border border-fendi/30 p-8 md:p-10 space-y-6">
                    <div data-id="faq-form-header" className="text-center mb-2">
                      <p data-id="faq-form-title" className="text-sm text-softblack font-medium">{placeholder("short")}</p>
                      <p data-id="faq-form-subtitle" className="text-xs text-warmgray mt-1.5">{placeholder("short")}</p>
                    </div>

                    <div data-id="faq-form-row" className="grid md:grid-cols-2 gap-5">
                      <label data-id="faq-field-name" className="block">
                        <span data-id="faq-field-name-label" className="eyebrow text-[10px] text-warmgray flex items-center gap-1.5 mb-2"><User size={11} /> {placeholder("short")}</span>
                        <input data-id="faq-input-name"
                          className="w-full bg-offwhite border border-fendi/30 focus:border-champagne outline-none px-4 py-3 text-sm text-softblack transition-colors"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder={placeholder("short")}
                        />
                      </label>
                      <label data-id="faq-field-email" className="block">
                        <span data-id="faq-field-email-label" className="eyebrow text-[10px] text-warmgray flex items-center gap-1.5 mb-2"><Mail size={11} /> {placeholder("short")}</span>
                        <input data-id="faq-input-email"
                          className="w-full bg-offwhite border border-fendi/30 focus:border-champagne outline-none px-4 py-3 text-sm text-softblack transition-colors"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder={placeholder("short")}
                          type="email"
                        />
                      </label>
                    </div>

                    <label data-id="faq-field-question" className="block">
                      <span data-id="faq-field-question-label" className="eyebrow text-[10px] text-warmgray flex items-center gap-1.5 mb-2"><MessageSquare size={11} /> {placeholder("short")}</span>
                      <textarea data-id="faq-input-question"
                        className="w-full bg-offwhite border border-fendi/30 focus:border-champagne outline-none px-4 py-3 text-sm text-softblack transition-colors resize-none"
                        rows={4}
                        value={form.question}
                        onChange={(e) => setForm({ ...form, question: e.target.value })}
                        placeholder={placeholder("short")}
                      />
                    </label>

                    <button data-id="faq-submit"
                      type="submit"
                      disabled={sending}
                      className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-softblack text-offwhite text-xs uppercase tracking-[0.2em] hover:bg-softblack/80 transition-colors disabled:opacity-50"
                    >
                      <Send size={12} /> {sending ? placeholder("short") : placeholder("short")}
                    </button>
                  </form>
                )}
              </Reveal>
            </>
          )}
      </div>
    </section>
  );
}
